import React, { useState, useEffect, useContext } from 'react';
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';
import './styles.css';
import { GetPostInCalendarMonth } from '../../Client/ManagementServiceClient';
import { AuthContext } from '../../context/auth';
import { formatDateTimeFromTicks } from '../../utils/formatters';

function Schedule() {
    const [date, setDate] = useState(new Date());
    const [posts, setPosts] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const { user } = useContext(AuthContext);

    // Function to get the start and end of the month for the given date
    const getMonthRange = (selectedDate) => {
        const startOfMonth = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), 1);
        const endOfMonth = new Date(selectedDate.getFullYear(), selectedDate.getMonth() + 1, 0, 23, 59, 59, 999);
        return { startOfMonth, endOfMonth };
    };

    // Fetch posts for the current month
    const fetchPostsForMonth = async (selectedDate) => {
        if (!user) {
            setError('User not authenticated');
            return;
        }

        setLoading(true);
        setError(null);
        
        try {
            const { startOfMonth, endOfMonth } = getMonthRange(selectedDate);
            const idToken = await user.getIdToken();
            const { status, myJson } = await GetPostInCalendarMonth(
                idToken, 
                startOfMonth, 
                endOfMonth
            );

            if (status === 200) {
                setPosts(myJson.Posts || []);
            } else {
                setError('Failed to fetch posts');
                console.error('Failed to fetch posts:', myJson);
            }
        } catch (err) {
            setError('Error fetching posts');
            console.error('Error fetching posts:', err);
        } finally {
            setLoading(false);
        }
    };

    // Fetch posts when component mounts or date changes month
    useEffect(() => {
        fetchPostsForMonth(date);
    }, [date.getMonth(), date.getFullYear()]); // eslint-disable-line react-hooks/exhaustive-deps

    // Convert tick-based PostAtTimeUTC to JavaScript Date for comparison
    const ticksToDate = (ticks) => {
        const ticksToUnixEpoch = 621355968000000000;
        const unixTimestampMs = (ticks - ticksToUnixEpoch) / 10000;
        return new Date(unixTimestampMs);
    };

    const onChange = (newDate) => {
        setDate(newDate);
    };

    const renderEvents = (selectedDate) => {
        if (loading) {
            return <div className="event">Loading events...</div>;
        }
        
        if (error) {
            return <div className="event error">Error: {error}</div>;
        }

        const eventsForDate = posts.filter(post => {
            const postDate = ticksToDate(post.PostAtTimeUTC);
            return postDate.toDateString() === selectedDate.toDateString();
        });

        if (eventsForDate.length === 0) {
            return <div className="no-events">No posts scheduled for this date</div>;
        }

        return eventsForDate.map((post, index) => (
            <div key={index} className="event">
                <div className="event-title">{post.Title}</div>
                <div className="event-time">
                    {formatDateTimeFromTicks(post.PostAtTimeUTC)}
                </div>
                <div className="event-description">{post.Description}</div>
            </div>
        ));
    };

    // Add indicators to calendar tiles that have posts
    const tileContent = ({ date, view }) => {
        if (view === 'month') {
            const hasEvents = posts.some(post => {
                const postDate = ticksToDate(post.PostAtTimeUTC);
                return postDate.toDateString() === date.toDateString();
            });
            
            return hasEvents ? <div className="event-indicator">•</div> : null;
        }
        return null;
    };

    return (
        <div className="schedule">
            <h1>Calendar</h1>
            {loading && <div className="loading">Loading posts for this month...</div>}
            <Calendar
                onChange={onChange}
                value={date}
                tileContent={tileContent}
            />
            <div className="events">
                <h2>Posts on {date.toDateString()}</h2>
                {renderEvents(date)}
            </div>
        </div>
    );
}

export { Schedule };